import { InputAdornment, TextField as MuiTextField } from "@mui/material";
import {
  type AriaAttributes,
  type ChangeEventHandler,
  type FocusEventHandler,
  type InputHTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from "react";

type InputLabelProps = {
  shrink?: boolean;
  hidden?: boolean;
};

type InputControlProps = {
  truncate?: boolean;
  noSelect?: boolean;
  align?: "start" | "end";
};

export type TextFieldProps = AriaAttributes & {
  value?: string | number;
  defaultValue?: string | number;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
  label?: ReactNode;
  placeholder?: string;
  error?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  type?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  inputProps?: Pick<
    InputHTMLAttributes<HTMLInputElement>,
    | "inputMode"
    | "pattern"
    | "min"
    | "max"
    | "step"
    | "maxLength"
    | "minLength"
    | "autoComplete"
    | "aria-label"
    | "aria-labelledby"
    | "aria-describedby"
    | "readOnly"
  >;
  inputRef?: Ref<HTMLInputElement>;
  inputControl?: InputControlProps;
  inputLabel?: InputLabelProps;
  name?: string;
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
  className?: string;
  minWidth?: string | number;
  size?: "small" | "medium";
  variant?: "outlined" | "filled" | "standard";
};

export function TextField({
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  onClick,
  onMouseDown,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  required,
  type,
  startAdornment,
  endAdornment,
  inputProps,
  inputRef,
  inputControl,
  inputLabel,
  name,
  id,
  autoComplete,
  autoFocus,
  readOnly,
  fullWidth,
  className,
  minWidth,
  size,
  variant,
  ...ariaProps
}: TextFieldProps) {
  return (
    <MuiTextField
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      onMouseDown={onMouseDown}
      label={label}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      disabled={disabled}
      required={required}
      type={type}
      name={name}
      id={id}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      fullWidth={fullWidth}
      hiddenLabel={inputLabel?.hidden}
      className={className}
      size={size}
      variant={variant}
      sx={{
        ...(minWidth !== undefined ? { minWidth } : {}),
        "& .MuiInputBase-input": {
          ...(inputControl?.truncate
            ? {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            : {}),
          ...(inputControl?.noSelect
            ? {
                userSelect: "none",
              }
            : {}),
          ...(inputControl?.align
            ? {
                textAlign: inputControl.align,
              }
            : {}),
        },
      }}
      slotProps={{
        htmlInput: {
          ...inputProps,
        },
        input: {
          inputRef,
          readOnly,
          startAdornment: startAdornment ? (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ) : undefined,
          endAdornment: endAdornment ? (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ) : undefined,
        },
        inputLabel:
          inputLabel?.shrink === undefined
            ? undefined
            : {
                shrink: inputLabel.shrink,
              },
      }}
      {...ariaProps}
    />
  );
}
